import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { api } from '~/lib/api-client';
import { approvalKeys, chatStatusKeys } from '~/lib/query-keys';
import { approvalsCacheDisagrees } from './approvals-reconcile';
import type { Approval } from './use-approvals';

/**
 * FRONT-661 — the chat-status spine, read side. One status per chat, derived
 * server-side from the run/needs-you signals + the per-user seen marker. This
 * is the single source the chat dot and the top-left indicator read.
 */
export type ChatStatusState =
  | 'working'
  | 'done-unseen'
  | 'done-seen'
  | 'needs-you'
  | 'failed';

export interface ChatStatus {
  sessionId: string;
  state: ChatStatusState;
  cause?: string;
  runId?: string;
  /**
   * FRONT-2343: mirrors `ChatStatus.pendingApprovalIds`
   * (src/agents/chat-status-store.ts) — ids of this session's pending,
   * visible approvals, present only when non-empty. Cross-replica
   * reconciliation signal, not something a card renders directly: see
   * `approvalsCacheDisagrees` (./approvals-reconcile.ts).
   */
  pendingApprovalIds?: string[];
  /** FRONT-2603: what a needs-you chat is waiting on, in its own words. */
  waitingOn?: { kind: 'question' | 'approval' | 'plan'; text: string };
}

interface StatusResponse {
  statuses: Record<string, ChatStatus>;
}

function isAuthError(err: unknown): boolean {
  return (err as { status?: number } | null)?.status === 401;
}

const ATTENTION: ReadonlySet<ChatStatusState> = new Set([
  'working',
  'needs-you',
]);

/**
 * The query options `useChatStatuses` below hands to `useQuery` — pulled out
 * as its own factory so a test can drive the real queryFn (including the
 * FRONT-2343 reconcile step) against a real `QueryClient` without rendering
 * any React (this suite runs under plain node, no jsdom, no effects — see
 * use-chat-status.test.ts).
 *
 * `pollWhile` (FRONT-2343): forces the 2s poll on top of the existing
 * active-status rule, for a caller whose OWN turn is known to be in flight
 * locally even though the cached status hasn't caught up yet — a second
 * message in an already-open chat starts from a cached "done" status, which
 * the active-status rule alone would never re-poll. Any surface that shows
 * a session's approvals and knows it may be waiting on one passes it.
 * `ChatMessages` (`frontend/src/features/chat/chat-messages.tsx`) does so from
 * `isTurnActive` (`turn-active-logic.ts`), so every chat surface it
 * mounts on gets this poll without wiring it per surface; a surface without
 * ChatMessages on screen (a minimized hands-free call, FRONT-3118) passes it
 * itself. Still false on an auth error regardless of `pollWhile`.
 */
export function chatStatusesQueryOptions(
  qc: QueryClient,
  ids: string[],
  options: { pollWhile?: boolean } = {},
) {
  const { pollWhile = false } = options;
  return {
    queryKey: chatStatusKeys.list(ids),
    queryFn: async () => {
      const qs =
        ids.length > 0
          ? `?sessionIds=${encodeURIComponent(ids.join(','))}`
          : '';
      const data = await api.get<StatusResponse>(`/api/chat-status${qs}`);
      // FRONT-2343 cross-replica reconcile — full explanation in
      // approvals-reconcile.ts. `cancelRefetch: false`: this query polls
      // every 2s while active, and cancelling an in-flight approvals fetch
      // on every tick would restart it forever and it would never complete.
      // If the approvals fetch keeps failing, the disagreement persists and
      // this re-invalidates on every poll — bounded to the 2s cadence and
      // deduped by React Query, accepted.
      if (
        approvalsCacheDisagrees(
          data.statuses,
          qc.getQueryData<Approval[]>(approvalKeys.list()),
        )
      ) {
        qc.invalidateQueries(
          { queryKey: approvalKeys.list() },
          { cancelRefetch: false },
        );
      }
      return data.statuses;
    },
    refetchInterval: (q: { state: { error: unknown; data: unknown } }) => {
      if (isAuthError(q.state.error)) return false;
      if (pollWhile) return 2_000;
      const statuses = q.state.data as Record<string, ChatStatus> | undefined;
      const anyActive = statuses
        ? Object.values(statuses).some((s) => ATTENTION.has(s.state))
        : false;
      return anyActive ? 2_000 : false;
    },
    retry: (_count: number, err: unknown) => !isAuthError(err),
  };
}

/**
 * Status for a set of chats (the launcher pills). Pass the session ids; omit to
 * let the server default to the user's active chats (the indicator). SSE keeps
 * it live; we also poll while any chat is working/needs-you as a fallback, or
 * while `pollWhile` says the caller's own turn is in flight locally
 * (FRONT-2343 — see `chatStatusesQueryOptions` above).
 */
export function useChatStatuses(
  sessionIds?: string[],
  options: { pollWhile?: boolean } = {},
) {
  const ids = sessionIds ?? [];
  const qc = useQueryClient();
  return useQuery({
    ...chatStatusesQueryOptions(qc, ids, options),
    // When specific ids are requested, only fetch once we actually have some.
    enabled: sessionIds === undefined || ids.length > 0,
  });
}

/**
 * Mark a chat seen (open = seen). Flips a green/red chat to gray server-side, so
 * it survives reload and greys on every device. Never clears amber.
 */
export function useMarkChatSeen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (sessionId: string) => {
      await api.post('/api/chat-status/seen', { sessionId });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: chatStatusKeys.all });
    },
  });
}
